import { AfterViewChecked, Component, Inject, ViewEncapsulation } from '@angular/core';
import { User } from '@shared/models/user';
import { CommonService } from '@core/services/common.service';
import { BaseModuleComponent } from '@modules/base-module/base-module.component';
import { Lob } from '@shared/models/lob-enum';
import { PaymentStatus, PaymentStatusVariant } from '@shared/models/payment-status';
import { GroupWithPolicies } from '@shared/models/group-w-policies';
import { InactiveProduct } from '@shared/models/inactive-product';
import { DataService } from '@core/services/data.service';
import { Router } from '@angular/router';
import { Policy } from '@shared/models/policy';
import { ActiveInsuranceGroup } from '@shared/models/active-insurance-group';
import { UserRole } from '@shared/models/codebooks/user-role';
import { LocaleService } from '@core/services/locale.service';
import { AgentService } from '@core/services/agent.service';
import { LoadingService } from '@core/services/loading.service';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss', '../../shared/shared-styles.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class HomeComponent extends BaseModuleComponent {

    public loggedUser: User;

    readonly lob: typeof Lob = Lob;
    readonly paymentStatusVariant: typeof PaymentStatusVariant = PaymentStatusVariant;
    readonly paymentStatus: typeof PaymentStatus = PaymentStatus;

    public groupsWPolicies: GroupWithPolicies[] = [];
    public inactiveProducts: InactiveProduct[];
    public specialOffer: InactiveProduct;
    protected activeCardsCount = 0;
    public inactiveCardsToFill = 0;

    public nxCol: string[] = ['',
        '12, 12, 12, 6, 4', // one of three columns
        '12, 12, 12, 12, 8', // two of three columns
        '12'];  // all row
    public loadMoreExpanded = false;

    public rows: number;
    public specialOfferRow: number;
    public specialOfferGroupsBefore: number;

    public welcomeName = '';

    constructor(
        public commonService: CommonService,
        protected dataService: DataService,
        protected router: Router,
        protected localeService: LocaleService,
        protected agentService: AgentService,
        protected loadingService: LoadingService
    ) {
        super(commonService);
        this.loggedUser = this.getLoggedUser();

        this.setWelcomeName();
        localStorage.removeItem('filterValues');

        this.rows = 1;
        this.specialOfferRow = 0;
        this.specialOfferGroupsBefore = 0;
        this.inactiveProducts = this.dataService.inactiveProducts;
    }

    openPolicyDetail(policy: Policy): void {
        this.router.navigate(['/moje-poistky', policy.policyState.name, policy.policyNumber]);
    }

    protected groupPolicies(policies: Policy[]) {
        let lastRowCount = 0;
        const groups = this.dataService.activeInsuranceGroups; // .sort((a, b) => a.position > b.position ? 1 : -1);

        groups.forEach((group: ActiveInsuranceGroup) => {
            if (!policies.some(policy => group.lobIds.some(x => x === policy.lineOfBusinessId))) {
                return;
            }

            const groupWithPolicies = new GroupWithPolicies(group);
            groupWithPolicies.policies = policies.filter(policy => {
                if (group.name === 'car-insurance-ek') {
                    return policy.isElectricVehicle;
                }
                else {
                    if (group.name === 'car-insurance') {
                        return group.lobIds.some(x => x === policy.lineOfBusinessId && policy.isElectricVehicle === false);
                    }
                    return group.lobIds.some(x => x === policy.lineOfBusinessId);
                }
            });

            // count policices for given lineOfBussinessId
            groupWithPolicies.count = groupWithPolicies.policies.length;
            // count cards, card group with 3+ cards has paged component
            groupWithPolicies.cols = groupWithPolicies.count > 3 ? 3 : groupWithPolicies.count;

            groupWithPolicies.count > 0 ? this.groupsWPolicies.push(groupWithPolicies) : null;
            this.activeCardsCount += groupWithPolicies.cols;
        });

        this.groupsWPolicies = this.groupsWPolicies
            .sort((x, y) => {
                return x.count === y.count ? x.position - y.position : y.count - x.count;
            });

        for (let i = 0; i < this.groupsWPolicies.length; i++) {
            // if last group has 2 columns and this group has more than 1, there will be empty space in that row
            // after 2 columns group. Fill that space with special offer
            if (lastRowCount === 2 && this.groupsWPolicies[i - 1].cols === 2 && this.groupsWPolicies[i].cols > 1) {
                this.specialOfferRow = this.rows;
            }
            if (this.specialOfferRow === 0) {
                this.specialOfferGroupsBefore++;
            }

            // count filled columns. Maximum is 3
            // if row is full, increase row counter
            const group = this.groupsWPolicies[i];
            if (lastRowCount + group.cols > 3) {
                this.rows++;
                lastRowCount = group.cols;
            }
            else {
                lastRowCount += group.cols;
            }
        }

        const lastRowCardsCount = (this.activeCardsCount + 1) % 3;
        this.inactiveCardsToFill = !!lastRowCardsCount ? 3 - lastRowCardsCount : 0;
        this.specialOffer = this.inactiveProducts.find(x => x.name === this.lob.TravelInsurance);
    }

    // opening modal to common service or modal service
    openLink(url: string, t = false): void {

        //any better solution?
        if (url === 'call-me-back') {
            this.commonService.openCallMeBackModal();
            return;
        }

        if (this.loggedUser.prefferedAgent == null || this.loggedUser.prefferedAgent.agentId == null || url === 'preffered-agent') {
            this.commonService.openPrefferedAgentModal(url);
            return;
        }

        if ((this.loggedUser.prefferedAgent != null || this.loggedUser.prefferedAgent.agentId != null) && t == true) {
            this.agentService.getPrefferedAgentToken(this.loggedUser.prefferedAgent.agentNo).subscribe(token => {
                window.open(url + '?agent=' + token['token'], '_blank');
            });
        } else {
            window.open(url, '_blank');
        }

    }

    private setWelcomeName() {
        this.welcomeName =
            ((this.loggedUser.firstName != null) ? this.loggedUser.firstName : '') + ' ' +
            ((this.loggedUser.lastName != null) ? this.loggedUser.lastName : '');

        if (this.loggedUser.role == UserRole.LegalPerson
            && this.loggedUser.firstName == null && this.loggedUser.lastName == null) {

            this.localeService.activeLanguage.subscribe(lang => {
                if (lang.code == 'SK') {
                    this.welcomeName = ', ' + this.loggedUser.vatNumber;
                } else {
                    this.welcomeName = ' ' + this.loggedUser.vatNumber;
                }
            });

        } else {
            this.welcomeName = ', ' + this.welcomeName;
        }
    }
}
